/**
 * src/services/api.js
 * Central API service — all backend calls go through here.
 */

const BASE_URL =
  import.meta.env.VITE_API_URL ||
  "https://lightslategrey-mole-360881.hostingersite.com/api";

async function request(endpoint, options = {}) {
  const token = localStorage.getItem("kkb_token");
  const config = {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    credentials: "include",
    ...options,
  };
  if (config.body && typeof config.body === "object") {
    config.body = JSON.stringify(config.body);
  }
  const res = await fetch(`${BASE_URL}${endpoint}`, config);
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || "Something went wrong");
  return data;
}

const get = (url, params = {}) => {
  const qs = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== ""),
    ),
  ).toString();
  return request(`${url}${qs ? "?" + qs : ""}`);
};
const post = (url, body) => request(url, { method: "POST", body });
const put = (url, body) => request(url, { method: "PUT", body });
const del = (url) => request(url, { method: "DELETE" });

// Add to your auth object
const auth = {
  register: (data) => post("/auth/register", data),
  login: (data) => post("/auth/login", data),
  logout: () => post("/auth/logout"),
  me: () => get("/auth/me"),
  updatePassword: (data) => put("/auth/update-password", data),
  // Add these two methods:
  forgotPassword: (email) => post("/auth/forgot-password", { email }),
  resetPassword: (token, password) =>
    post(`/auth/reset-password/${token}`, { password }),
};

const categories = {
  getNavigation: () => get("/categories/navigation"),
  getAll: () => get("/categories"),
  getBySlug: (slug) => get(`/categories/${slug}`),
  create: (data) => post("/categories", data),
  update: (id, data) => put(`/categories/${id}`, data),
  delete: (id) => del(`/categories/${id}`),
};

const products = {
  getAll: (params = {}) => get("/products", params),
  getById: (id) => get(`/products/${id}`),
  getCategories: () => get("/products/categories"),
  addReview: (id, data) => post(`/products/${id}/reviews`, data),
  create: (data) => post("/products", data),
  update: (id, data) => put(`/products/${id}`, data),
  remove: (id) => del(`/products/${id}`),
};

const cart = {
  get: () => get("/cart"),
  add: (productId, qty, size) => post("/cart", { productId, qty, size }),
  update: (key, qty) => put(`/cart/${encodeURIComponent(key)}`, { qty }),
  remove: (key) => del(`/cart/${encodeURIComponent(key)}`),
  clear: () => del("/cart"),
  sync: (items) => post("/cart/sync", { items }),
};

const wishlist = {
  get: () => get("/wishlist"),
  toggle: (id) => post(`/wishlist/${id}`),
  clear: () => del("/wishlist"),
};

const orders = {
  place: (data) => post("/orders", data),
  myOrders: () => get("/orders/my"),
  track: (orderId) => get(`/orders/track/${orderId}`),
  getById: (id) => get(`/orders/${id}`),
  cancel: (id, reason) => put(`/orders/${id}/cancel`, { reason }),
  getAll: (params = {}) => get("/orders", params),
  updateStatus: (id, data) => put(`/orders/${id}/status`, data),
  // Add Razorpay methods
  createRazorpayOrder: (data) => post("/orders/create-razorpay-order", data),
  verifyPayment: (data) => post("/orders/verify-payment", data),
};

const users = {
  getProfile: () => get("/users/profile"),
  updateProfile: (data) => put("/users/profile", data),
  getAddresses: () => get("/users/addresses"),
  addAddress: (data) => post("/users/addresses", data),
  updateAddress: (id, data) => put(`/users/addresses/${id}`, data),
  deleteAddress: (id) => del(`/users/addresses/${id}`),
};

const coupons = {
  validate: (code, orderTotal) =>
    post("/coupons/validate", { code, orderTotal }),
};

const contact = {
  send: (data) => post("/contact", data),
};

const api = {
  auth,
  products,
  cart,
  wishlist,
  orders,
  users,
  coupons,
  contact,
  categories,
};
export default api;
